import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SaasDashboard } from "@/components/SaasDashboard";
import { BuyerDashboard } from "@/components/BuyerDashboard";
import { PageHeader } from "@/components/kit/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { DateRangeFilter, useDateRange, rangeLabel } from "@/components/kit/DateRangeFilter";
import { MetricChart } from "@/components/kit/MetricChart";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { WithdrawFlow } from "@/drawers/WithdrawFlow";
import { fmtMoney, fmtNumber, fmtDateTime, getDisplayTimezone } from "@/lib/format";
import {
  DollarSign, TrendingUp, MousePointerClick, Activity, Lock, Wallet, Banknote, Percent, UserPlus, XCircle,
} from "lucide-react";

const ROLE_NOTE = {
  super_admin: "Showing data across all tenants.",
  tenant_admin: "Showing data for your tenant.",
  manager: "Showing data for your buyers.",
  buyer: "Showing your personal data.",
};

function TopList({ title, items, money = true, testId }) {
  const max = Math.max(...(items || []).map((i) => i.value), 1);
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={testId}>
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <div className="mt-4 space-y-3">
        {(!items || items.length === 0) && <p className="text-sm text-muted-foreground">No data yet.</p>}
        {(items || []).map((i, idx) => (
          <div key={idx} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="truncate text-foreground/90">{i.name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {money ? fmtMoney(i.value) : fmtNumber(i.value)}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-white/[0.05]">
              <div className="h-full rounded-full bg-white/40" style={{ width: `${(i.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <SaasDashboard />;
  if (user?.role === "buyer") return <BuyerDashboard />;
  return <TenantDashboard />;
}

function TenantDashboard() {
  const { user } = useAuth();
  const range = useDateRange("dash-range-perf");
  const params = { from: range.from, to: range.to, tz: getDisplayTimezone() };
  const { data, isLoading } = useQuery({ queryKey: ["dashboard", range.from, range.to, params.tz], queryFn: async () => (await http.get("/dashboard", { params })).data });
  const { data: stats } = useQuery({ queryKey: ["statistics", range.from, range.to, params.tz], queryFn: async () => (await http.get("/statistics", { params })).data });
  const { data: bal } = useQuery({ queryKey: ["balances", "me"], queryFn: async () => (await http.get("/conversions/balances")).data });
  const [wdOpen, setWdOpen] = useState(false);

  const c = data?.cards || {};
  const cards = [
    { label: "Clicks", value: fmtNumber(c.clicks), icon: MousePointerClick, accent: "#A1A1AA" },
    { label: "Leads", value: fmtNumber(c.leads), icon: UserPlus, accent: "#3B82F6" },
    { label: "Deposits", value: fmtNumber(c.deposits), icon: Banknote, accent: "#22C55E" },
    { label: "Rejects", value: fmtNumber(c.rejects), icon: XCircle, accent: "#EF4444" },
    { label: "Revenue", value: fmtMoney(c.revenue), icon: DollarSign, accent: "#22C55E" },
    { label: "Profit", value: fmtMoney(c.profit), icon: TrendingUp, accent: "#14B8A6" },
    { label: "CR", value: `${fmtNumber(c.cr)}%`, icon: Percent, accent: "#A1A1AA" },
    { label: "EPC", value: fmtMoney(c.epc), icon: Activity, accent: "#A1A1AA" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} subtitle={ROLE_NOTE[user?.role]} />
        <DateRangeFilter range={range} />
      </div>

      {/* Personal affiliate balances — managers/admins can also run traffic */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="staff-affiliate-balances">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-4" data-testid="staff-hold-balance">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> My Hold Balance</div>
          <div className="mt-1 text-2xl font-semibold text-[#EAB308]">{fmtMoney(bal?.hold_balance)}</div>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-4" data-testid="staff-available-balance">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> My Available Balance</div>
          <div className="mt-1 text-2xl font-semibold text-[#22C55E]">{fmtMoney(bal?.available_balance)}</div>
        </div>
        <div className="flex items-center justify-center rounded-xl border border-dashed border-white/[0.12] bg-[#0A0A0A] p-4">
          <Button onClick={() => setWdOpen(true)} disabled={!(bal?.available_balance > 0)} data-testid="staff-request-withdrawal-btn" className="gap-1.5 bg-[#22C55E] text-black hover:bg-[#16a34a] disabled:opacity-40"><Banknote className="h-4 w-4" /> Request Withdrawal</Button>
        </div>
      </div>
      <WithdrawFlow open={wdOpen} onOpenChange={setWdOpen} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((card, i) => (
          <StatCard key={card.label} {...card} delay={i * 40} testId={`stat-${card.label.toLowerCase().replace(" ", "-")}`} />
        ))}
      </div>

      {/* Chart */}
      <MetricChart
        title={`Performance — ${rangeLabel(range)}`}
        data={stats?.series || []}
        metrics={[
          { key: "clicks", label: "Clicks", color: "#A1A1AA" },
          { key: "leads", label: "Leads", color: "#3B82F6" },
          { key: "deposits", label: "Deposits", color: "#22C55E" },
          { key: "revenue", label: "Revenue", color: "#14B8A6" },
          { key: "profit", label: "Profit", color: "#EAB308" },
        ]}
        defaultActive={["clicks", "deposits", "profit"]}
        testId="dashboard-chart"
      />

      {/* Top lists */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TopList title="Top Buyers" items={data?.top_buyers} testId="top-buyers" />
        <TopList title="Top Managers" items={data?.top_managers} testId="top-managers" />
        <TopList title="Top Offers" items={data?.top_offers} testId="top-offers" />
        <TopList title="Top Advertisers" items={data?.top_advertisers} testId="top-advertisers" />
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="recent-conversions">
          <h3 className="mb-3 text-sm font-medium text-foreground">Latest Conversions</h3>
          <div className="space-y-1">
            {(data?.recent_conversions || []).map((cv) => (
              <div key={cv.id} className="flex items-center justify-between border-b border-white/[0.05] py-2 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="truncate text-foreground/90">{cv.offer_name}</div>
                  <div className="text-xs text-muted-foreground">{cv.buyer_name} · {cv.country}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#22C55E]">{fmtMoney(cv.payout)}</span>
                  <StatusBadge status={cv.status} />
                </div>
              </div>
            ))}
            {(data?.recent_conversions || []).length === 0 && <p className="text-sm text-muted-foreground">No conversions yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="recent-withdrawals">
          <h3 className="mb-3 text-sm font-medium text-foreground">Latest Withdrawals</h3>
          <div className="space-y-1">
            {(data?.recent_withdrawals || []).map((w) => (
              <div key={w.id} className="flex items-center justify-between border-b border-white/[0.05] py-2 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="truncate text-foreground/90">{w.user_name}</div>
                  <div className="text-xs text-muted-foreground">{w.method} · {fmtDateTime(w.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-foreground">{fmtMoney(w.amount)}</span>
                  <StatusBadge status={w.status} />
                </div>
              </div>
            ))}
            {(data?.recent_withdrawals || []).length === 0 && <p className="text-sm text-muted-foreground">No withdrawals yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
